'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useWishlist } from '@/hooks/useWishlist';
import { useOffers } from '@/hooks/useOffers';
import { useEnquiries } from '@/hooks/useEnquiries';
import { useBookings } from '@/hooks/useBookings';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { ConciergeWidget } from '../dashboard/ConciergeWidget';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import { Button } from '../ui/Button';
import {
  Activity,
  ArrowRight,
  Home,
  MessageSquare,
  Clock,
  MapPin,
  CloudSun,
  Gavel,
  ChevronRight,
  Calendar,
  Heart,
} from 'lucide-react';

export const Dashboard: React.FC = () => {
  const pathname = usePathname();
  const { customer } = useCustomerAuth();
  
  // Extract locale from pathname and build locale-prefixed href
  const getHref = (path: string): string => {
    const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
    const locale = match ? match[1] : 'en';
    return `/${locale}${path}`;
  };
  const { wishlists } = useWishlist();
  const { offers } = useOffers();
  const { enquiries } = useEnquiries();
  const { bookings } = useBookings();

  // Calculate stats (with defensive checks for undefined arrays)
  const wishlistsArray = wishlists || [];
  const offersArray = offers || [];
  const enquiriesArray = enquiries || [];
  const bookingsArray = bookings || [];
  
  const savedCount = wishlistsArray.reduce((acc, w) => acc + (w?.itemCount || 0), 0);
  const activeOffers = offersArray.filter(
    (o) =>
      !['COMPLETED', 'REJECTED', 'WITHDRAWN_BY_BUYER', 'WITHDRAWN_BY_VENDOR', 'EXPIRED'].includes(
        o.status
      )
  );
  const openEnquiries = enquiriesArray.filter((e) => e.status !== 'CLOSED');
  const upcomingBookings = bookingsArray.filter((b) => b.status === 'PENDING' || b.status === 'CONFIRMED');
  const nextBooking = upcomingBookings[0];

  // Get latest offer and enquiry
  const latestOffer = activeOffers[0];
  const latestEnquiry = openEnquiries[0];

  const firstName = customer?.firstName
    || customer?.displayName?.split(' ')[0]
    || 'there';

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 animate-[fadeIn_0.3s_ease-out]">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold text-stone-900 tracking-tight flex items-center gap-3">
            Hello, {firstName} <span className="text-2xl animate-pulse">👋</span>
          </h1>
          <p className="text-stone-500 mt-2 text-lg">Here is your property portfolio overview.</p>
        </div>
        <div className="flex items-center gap-2 text-stone-500 bg-white px-4 py-2 rounded-full shadow-sm border border-stone-100 transition-transform hover:scale-105 cursor-default">
          <CloudSun size={18} />
          <span className="text-sm font-medium">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
        </div>
      </div>

      {/* Bento Grid Layout - 4 Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Saved Properties - Large Visual Card (2x2) */}
        <Link
          href={getHref('/account/wishlists')}
          className="col-span-1 md:col-span-2 lg:col-span-2 row-span-2 relative group cursor-pointer rounded-3xl overflow-hidden shadow-sm border border-stone-200 h-[400px] lg:h-auto animate-[fadeIn_0.4s_ease-out]"
        >
          {savedCount > 0 ? (
            <>
              {/* Placeholder Image */}
              <div className="absolute inset-0 bg-stone-900">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-800 to-stone-900" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent z-20" />
              </div>

              <div className="absolute bottom-0 left-0 p-8 w-full z-30">
                <div className="flex items-center gap-2 mb-3 text-white/80 text-sm font-medium uppercase tracking-wider">
                  <Home size={16} />
                  <span>Saved Properties</span>
                </div>

                <div className="flex justify-between items-end">
                  <div>
                    <div className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-2 tracking-tight">
                      <AnimatedCounter end={savedCount} />
                    </div>
                    <p className="text-white/90 font-medium text-lg">Properties in your wishlist</p>
                  </div>
                  <div className="h-12 w-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white group-hover:bg-white group-hover:text-black transition-all duration-300">
                    <ArrowRight size={24} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-stone-900 h-full w-full p-8 flex flex-col justify-between relative overflow-hidden">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-stone-800 rounded-full blur-3xl -mr-32 -mt-32 opacity-50" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-stone-800 rounded-full blur-2xl -ml-24 -mb-24 opacity-30" />

              <div className="relative z-10 flex flex-col justify-between h-full">
                <div className="flex items-center gap-2 text-white/40 text-sm font-medium uppercase tracking-wider">
                  <Home size={16} />
                  <span>Saved Properties</span>
                </div>

                <div className="flex flex-col items-start gap-4 mb-4">
                  <div className="p-3 bg-stone-800/50 rounded-2xl border border-stone-700/50 backdrop-blur-sm group-hover:scale-110 transition-transform duration-500">
                    <Heart size={28} className="text-stone-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-light text-white mb-2">Your wishlist is empty</h3>
                    <p className="text-stone-400 text-sm leading-relaxed max-w-xs">
                      Start curating your portfolio. Save properties you love to track them here.
                    </p>
                  </div>
                </div>

                <Link href={getHref('/properties')}>
                  <Button
                    variant="secondary"
                    className="!bg-white/10 !text-white hover:!bg-white hover:!text-stone-900 border border-white/10 backdrop-blur-sm w-fit transition-all duration-300"
                  >
                    Explore Properties
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </Link>

        {/* Concierge Widget - Large (2x2) */}
        <div className="col-span-1 md:col-span-2 lg:col-span-2 row-span-2 animate-[fadeIn_0.5s_ease-out] h-[500px] lg:h-auto">
          <ConciergeWidget />
        </div>

        {/* Active Offers - The Transaction Ticket (2x1) */}
        <Link
          href={getHref('/account/offers')}
          className="col-span-1 md:col-span-2 lg:col-span-2 bg-white rounded-3xl border border-stone-200 shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer animate-[fadeIn_0.6s_ease-out] group overflow-hidden flex flex-col md:flex-row h-auto md:h-64 relative"
        >
          {latestOffer ? (
            <>
              {/* Left Panel: Visual Context */}
              <div className="md:w-5/12 relative h-48 md:h-full overflow-hidden bg-stone-200">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />

                <div className="absolute bottom-6 left-6 right-6">
                  <div className="inline-flex items-center gap-2 px-2 py-1 bg-white/20 backdrop-blur-md border border-white/10 rounded-md text-[10px] font-bold text-white uppercase tracking-widest mb-2">
                    ID: {latestOffer.id.slice(0, 8).toUpperCase()}
                  </div>
                  <h3 className="text-xl font-semibold text-white leading-tight shadow-sm truncate">
                    Active Offer
                  </h3>
                </div>
              </div>

              {/* Right Panel: Deal Physics */}
              <div className="md:w-7/12 p-6 md:p-8 flex flex-col justify-between relative bg-white">
                {/* Header Row */}
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Activity
                        size={14}
                        className={
                          latestOffer.status === 'COUNTERED' ? 'text-amber-500' : 'text-green-500'
                        }
                      />
                      <span className="text-xs font-bold text-stone-400 uppercase tracking-widest">
                        Current Position
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl md:text-4xl font-light text-stone-900 tracking-tight">
                        {latestOffer.status}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`shrink-0 px-3 py-1.5 rounded-full flex items-center gap-2 border ${
                      latestOffer.status === 'COUNTERED'
                        ? 'bg-amber-50 border-amber-200 text-amber-700'
                        : latestOffer.status === 'ACCEPTED'
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-stone-50 border-stone-200 text-stone-600'
                    }`}
                  >
                    <span className="relative flex h-2 w-2">
                      <span
                        className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                          latestOffer.status === 'COUNTERED'
                            ? 'bg-amber-500'
                            : latestOffer.status === 'ACCEPTED'
                              ? 'bg-green-500'
                              : 'bg-stone-500'
                        }`}
                      />
                      <span
                        className={`relative inline-flex rounded-full h-2 w-2 ${
                          latestOffer.status === 'COUNTERED'
                            ? 'bg-amber-500'
                            : latestOffer.status === 'ACCEPTED'
                              ? 'bg-green-500'
                              : 'bg-stone-500'
                        }`}
                      />
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide hidden sm:inline">
                      {latestOffer.status === 'COUNTERED' ? 'Counter Active' : latestOffer.status}
                    </span>
                  </div>
                </div>

                {/* Action Row */}
                <div className="mt-6 flex items-end justify-between border-t border-stone-100 pt-6">
                  <div className="space-y-1">
                    <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wider">
                      Latest Update
                    </p>
                    <p className="text-xs font-medium text-stone-900 flex items-center gap-2">
                      {latestOffer.statusReason || `Status: ${latestOffer.status}`}
                      <span className="text-stone-300">•</span>
                      <span className="text-stone-500 font-normal">
                        {new Date(latestOffer.updatedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="group/btn pl-0 hover:bg-transparent hover:text-stone-600 text-stone-900 font-semibold text-sm transition-all p-0 h-auto"
                  >
                    Open Deal Room{' '}
                    <ArrowRight
                      size={16}
                      className="ml-2 transition-transform group-hover/btn:translate-x-1"
                    />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full bg-white p-8 flex flex-col md:flex-row relative overflow-hidden group">
              {/* Decorative background */}
              <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-stone-50 via-white to-white opacity-80" />

              <div className="relative z-10 flex-1 flex flex-col justify-center items-center text-center">
                <div className="w-16 h-16 bg-stone-50 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-stone-100 group-hover:scale-110 transition-transform duration-500 group-hover:bg-white group-hover:shadow-md">
                  <Gavel
                    size={28}
                    className="text-stone-300 group-hover:text-stone-900 transition-colors duration-500"
                  />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">No Active Negotiations</h3>
                <p className="text-stone-500 text-sm max-w-sm mx-auto mb-6 leading-relaxed">
                  Ready to acquire your next asset? Make an offer on a property to start tracking
                  negotiations here.
                </p>
                <Link href={getHref('/properties')}>
                  <Button
                    variant="outline"
                    className="border-stone-200 text-stone-600 hover:text-stone-900 hover:border-stone-900 transition-all"
                  >
                    Browse Market
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </Link>

        {/* Stats - Enquiries (1x1) */}
        <Link
          href={getHref('/account/enquiries')}
          className="col-span-1 bg-white rounded-3xl border border-stone-200 shadow-sm hover:shadow-2xl hover:border-stone-300 transition-all duration-500 cursor-pointer animate-[fadeIn_0.7s_ease-out] group relative overflow-hidden h-64 flex flex-col"
        >
          {/* Dynamic Background */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-stone-100 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl -mr-10 -mt-10" />

          <div className="p-6 flex-1 flex flex-col relative z-10">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-stone-50 text-stone-600 rounded-2xl group-hover:bg-stone-900 group-hover:text-white transition-all duration-500 border border-stone-100 group-hover:scale-110 shadow-sm">
                <MessageSquare size={22} />
              </div>
              {openEnquiries.length > 0 ? (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-600 rounded-full border border-blue-100">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wide">Active</span>
                </div>
              ) : (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-stone-100 p-1.5 rounded-full text-stone-400">
                  <ArrowRight size={16} />
                </div>
              )}
            </div>

            {/* Stat */}
            <div className="mb-auto">
              <p className="text-stone-500 font-medium text-xs uppercase tracking-wider mb-0.5">
                Inbox
              </p>
              <div className="text-4xl font-light text-stone-900 tracking-tight">
                <AnimatedCounter end={openEnquiries.length} />
              </div>
            </div>

            {/* Latest Message */}
            <div className="mt-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500 ease-out">
              {latestEnquiry ? (
                <div className="relative">
                  <div className="bg-white border border-stone-200 shadow-sm rounded-xl p-3.5 relative z-10 group-hover:shadow-lg group-hover:border-stone-300 transition-all duration-300">
                    <div className="absolute -top-1.5 left-4 w-3 h-3 bg-white border-t border-l border-stone-200 transform rotate-45 group-hover:border-stone-300 transition-colors duration-300" />
                    <p className="text-xs text-stone-500 line-clamp-2">{latestEnquiry.subject}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center opacity-60">
                  <p className="text-xs font-medium text-stone-400">No new messages</p>
                </div>
              )}
            </div>
          </div>
        </Link>

        {/* Upcoming Schedule (1x1) */}
        <div className="col-span-1 bg-white border border-stone-200 rounded-3xl p-6 animate-[fadeIn_0.8s_ease-out] flex flex-col justify-between hover:shadow-md transition-shadow h-64 group cursor-pointer">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-stone-100 rounded-lg text-stone-600">
                <Calendar size={18} />
              </div>
              <h3 className="font-semibold text-stone-900 uppercase tracking-wide text-xs">
                Schedule
              </h3>
            </div>
            {nextBooking && (
              <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                UPCOMING
              </span>
            )}
          </div>

          {nextBooking ? (
            <div className="flex flex-col gap-3 mt-1">
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-center justify-center h-14 w-14 bg-stone-50 rounded-xl border border-stone-100 shrink-0 shadow-sm">
                  <span className="text-[10px] font-bold text-red-500 uppercase">
                    {new Date(nextBooking.scheduledAt).toLocaleString('default', { month: 'short' })}
                  </span>
                  <span className="text-xl font-bold text-stone-900 leading-none">
                    {new Date(nextBooking.scheduledAt).getDate()}
                  </span>
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-bold text-stone-900 truncate">
                    {nextBooking.bookingType}
                  </h4>
                  <span className="text-xs text-stone-500 block truncate">Viewing</span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-stone-500 bg-stone-50 p-2 rounded-lg border border-stone-100">
                <Clock size={12} />
                <span className="font-medium">
                  {new Date(nextBooking.scheduledAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="text-stone-300">|</span>
                <MapPin size={12} />
                <span>{nextBooking.duration} mins</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-4">
              <div className="w-12 h-12 bg-stone-50 rounded-full flex items-center justify-center mb-3 border border-stone-100 group-hover:scale-110 transition-transform">
                <Calendar size={20} className="text-stone-300 group-hover:text-stone-400" />
              </div>
              <h4 className="text-sm font-bold text-stone-900">No Upcoming Viewings</h4>
              <p className="text-xs text-stone-500 mt-1 max-w-[150px]">
                Your schedule is clear for the week.
              </p>
              <Link href={getHref('/properties')}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-stone-600 hover:text-stone-900 bg-stone-50 hover:bg-stone-100 rounded-lg"
                >
                  Book Viewing
                </Button>
              </Link>
            </div>
          )}

          {nextBooking && (
            <div className="mt-auto pt-2 border-t border-stone-50">
              <Link href={getHref('/account/bookings')}>
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  className="justify-between text-stone-500 hover:text-stone-900 px-0 hover:bg-transparent"
                >
                  View Calendar <ChevronRight size={14} />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
